import { getStoredGclid } from './gclid-tracker';

/**
 * Analytics events are pushed to the GTM dataLayer, not sent through gtag().
 *
 * GA4 is configured inside the GTM container (GTM-N5GK6ZX8) rather than on the
 * page, so no measurement ID appears in this codebase — GTM decides which
 * property receives the data. Calling gtag('event', ...) would require a
 * gtag('config', '<measurement-id>') on the page, which is exactly the
 * duplicate tag that was removed.
 *
 * IMPORTANT: a dataLayer push only raises an event inside GTM. It does not
 * reach GA4 on its own. Every event below needs a Custom Event trigger plus a
 * GA4 Event tag in the container, with Data Layer Variables for the parameters.
 * Until that exists these events go nowhere — verify in GTM Preview and GA4
 * DebugView, not by reading this file.
 *
 * The route-change event is deliberately NOT named `page_view`. GA4's config
 * tag already emits a page view when the container loads, so an app event of
 * the same name would double-count the first route. Map `spa_page_view` to a
 * GA4 page_view tag and turn off automatic page views on the config tag, and
 * every route — first included — is counted exactly once.
 */
function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export function trackPageView(page: string, referrer?: string | null): void {
  const gclid = getStoredGclid();

  pushToDataLayer({
    event: 'spa_page_view',
    page_path: page,
    page_referrer: referrer || undefined,
    gclid: gclid || undefined,
  });
}

export function trackSignupConversion(email: string, gclid?: string | null): void {
  const storedGclid = gclid || getStoredGclid();

  pushToDataLayer({
    event: 'conversion',
    conversion_type: 'signup',
    value: 1.0,
    currency: 'USD',
    transaction_id: `signup_${Date.now()}`,
    gclid: storedGclid || undefined,
  });

  pushToDataLayer({
    event: 'sign_up',
    method: 'email',
    gclid: storedGclid || undefined,
  });

  console.log('[Analytics] Signup conversion tracked', { email, gclid: storedGclid });
}

export function trackEvent(
  eventName: string,
  params?: Record<string, any>
): void {
  const gclid = getStoredGclid();

  pushToDataLayer({
    event: eventName,
    ...params,
    gclid: gclid || undefined,
  });

  console.log('[Analytics] Event tracked:', eventName, params);
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}
